import { useEffect, useMemo, useState } from "react";
import { useParams } from "@tanstack/react-router";
import { getUser, getUsersApi, updateUser, useCore } from "@praesto/core";
import type { Skill, SkillGroup } from "@praesto/shared-types";
import {
  Input,
  Badge,
  Chip,
  EmptyState,
  SkeletonList,
  SkeletonCardListItem,
} from "@praesto/ui-kit";
import { profileEditScope } from "./profile-edit-scope";

const groupLabels: Record<SkillGroup, string> = {
  operative: "Operative",
  language: "Lingue",
  certification: "Certificazioni",
  custom: "Personalizzate",
};

const groupOrder: SkillGroup[] = ["operative", "language", "certification", "custom"];

// The user object returned by the API carries extra fields beyond the typed UserSummary
interface UserDetailWithSkills {
  id: string;
  email: string;
  displayName: string;
  status: "pending" | "active" | "disabled" | "archived";
  skills?: string[];
}

export function ProfileSkillsTab() {
  const core = useCore();
  const { uid } = useParams({ strict: false }) as { uid: string };
  const [taxonomy, setTaxonomy] = useState<Skill[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [memberships, setMemberships] = useState<ReadonlyArray<{ role: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [search, setSearch] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const api = getUsersApi();
      const tok = await api.getAuthToken();
      const [userRes, taxoRes] = await Promise.all([
        getUser(api.apiBase, uid, tok),
        fetch(`${api.apiBase}/api/users/skills-taxonomy`, {
          headers: { Authorization: `Bearer ${tok}` },
        }).then((r) => r.json() as Promise<{ skills: Skill[] }>),
      ]);
      if (!cancelled) {
        const userSkills = (userRes.user as UserDetailWithSkills).skills ?? [];
        setSelected(new Set(userSkills));
        setMemberships(userRes.memberships);
        setTaxonomy(taxoRes.skills);
      }
    })()
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [uid]);

  const grouped = useMemo(() => {
    const map = new Map<SkillGroup, Skill[]>();
    for (const s of taxonomy) {
      if (search && !s.label.toLowerCase().includes(search.toLowerCase())) continue;
      const arr = map.get(s.group) ?? [];
      arr.push(s);
      map.set(s.group, arr);
    }
    return map;
  }, [taxonomy, search]);

  async function save(newSet: Set<string>) {
    setSaveStatus("saving");
    try {
      const api = getUsersApi();
      const tok = await api.getAuthToken();
      await updateUser(
        api.apiBase,
        uid,
        { skills: Array.from(newSet) } as Parameters<typeof updateUser>[2],
        tok,
      );
      setSaveStatus("saved");
      setTimeout(() => setSaveStatus("idle"), 1500);
    } catch {
      setSaveStatus("error");
    }
  }

  function toggle(id: string) {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
    void save(next);
  }

  // PUT /:uid refuses skills on yourself and on an owner unless you are the
  // owner (C-174, C-175): the chips stay visible but stop being buttons.
  const scope = profileEditScope({
    viewerIsOwner: core.rbac.can("manage", "hotel"),
    isSelf: uid === core.auth.currentUserId(),
    memberships,
  });
  const canEditSkills = scope.canEdit("skills");

  if (loading) return <SkeletonList count={5} item={<SkeletonCardListItem />} />;

  if (taxonomy.length === 0)
    return (
      <EmptyState
        title="Nessuna competenza configurata"
        description="Le competenze di questo account non sono ancora state definite: chiedi al titolare di configurarle."
        size="compact"
      />
    );

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="flex items-center gap-3">
        <Input
          size="md"
          placeholder="Cerca una competenza…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1 sm:max-w-xs"
        />
        <div className="h-6 flex items-center">
          {saveStatus === "saving" && (
            <Badge tone="neutral" variant="soft" size="xs">
              Salvataggio…
            </Badge>
          )}
          {saveStatus === "saved" && (
            <Badge tone="success" variant="soft" size="xs">
              Salvato
            </Badge>
          )}
          {saveStatus === "error" && (
            <Badge tone="danger" variant="soft" size="xs">
              Errore
            </Badge>
          )}
        </div>
      </div>

      <div className="text-caption text-fg-muted">
        {selected.size} selezionate · {taxonomy.length} disponibili
      </div>

      {scope.reason && (
        <p className="text-caption text-fg-muted">
          {core.i18n.t(`people.profile.readOnly.${scope.reason}`)}
        </p>
      )}

      {groupOrder.map((g) => {
        const items = grouped.get(g);
        if (!items || items.length === 0) return null;
        return (
          <section key={g} className="space-y-2">
            <div className="text-eyebrow">{groupLabels[g]}</div>
            <div className="flex flex-wrap gap-2">
              {items.map((s) => (
                <Chip
                  key={s.id}
                  selected={selected.has(s.id)}
                  {...(canEditSkills ? { onClick: () => toggle(s.id) } : {})}
                >
                  {s.label}
                </Chip>
              ))}
            </div>
          </section>
        );
      })}

      {search && grouped.size === 0 && (
        <EmptyState
          title="Nessuna corrispondenza"
          description={`Nessuna competenza trovata per "${search}". Prova con un altro termine o svuota la ricerca.`}
          size="compact"
        />
      )}
    </div>
  );
}
